import { createSlice } from '@reduxjs/toolkit'

export const createCrudSlice = ({
  name,
  initialState = {},
  reducers = {},
  max = -1,
  sort = 1,
  idKey = '_id',
}) => {
  const crudInitialState = {
    total: -1,
    limit: -1,
    fetching: null,
    error: null,
    problem: null,
    query: null,
    queryIds: [],
  }
  crudInitialState[name] = []

  const nullReducer = (state) => state
  const successReducer = (state, { payload }) => {
    const index = state[name].findIndex((e) => e[idKey] === payload[idKey])
    const crudState = {}
    if (index > -1) {
      crudState[name] = [...state[name].slice(0, index), payload, ...state[name].slice(index + 1)]
    } else if (sort === -1) {
      crudState[name] = [payload, ...state[name]]
    } else {
      crudState[name] = state[name].concat(payload)
    }
    if (max > -1 && crudState[name].length > max) {
      if (sort === -1) {
        crudState[name].splice(crudState[name].length - max)
      } else {
        crudState[name].splice(0, crudState[name].length - max)
      }
    }
    return { ...state, fetching: false, error: null, ...crudState }
  }
  const failureReducer = (state, { payload }) => {
    return { ...state, fetching: false, error: true, problem: payload }
  }

  const crudReducers = {
    findRequest: (state, { payload }) => {
      return { ...state, fetching: true, error: null, query: payload && payload.query }
    },
    findSuccess: (state, { payload }) => {
      const data = payload.data || payload
      const crudState = {
        queryIds: [],
        total: payload.total || state.total,
        limit: payload.limit || state.limit,
      }
      crudState[name] = [...state[name]]
      for (const val of data) {
        const index = crudState[name].findIndex((e) => e[idKey] === val[idKey])
        if (index > -1) {
          crudState[name] = [
            ...crudState[name].slice(0, index),
            val,
            ...crudState[name].slice(index + 1),
          ]
        } else {
          crudState[name] = crudState[name].concat(val)
        }
        if (state.query)
          crudState.queryIds.push(val[idKey])
      }
      if (max > -1 && crudState[name].length > max) {
        crudState[name].splice(crudState[name].length - max)
      }
      return { ...state, fetching: false, error: null, ...crudState }
    },
    findFailure: failureReducer,

    getRequest: (state) => {
      return { ...state, fetching: true, error: null }
    },
    getSuccess: successReducer,
    getFailure: failureReducer,

    createRequest: nullReducer,
    createSuccess: successReducer,
    createFailure: failureReducer,

    updateRequest: nullReducer,
    updateSuccess: successReducer,
    updateFailure: failureReducer,

    deleteRequest: nullReducer,
    deleteSuccess: (state, { payload }) => {
      const index = state[name].findIndex((e) => e[idKey] === payload[idKey])
      if (index > -1) {
        const crudState = {}
        crudState[name] = [...state[name].slice(0, index), ...state[name].slice(index + 1)]
        return { ...state, error: null, ...crudState }
      }
      return { ...state, error: null }
    },
    deleteFailure: failureReducer,
    watchRequest: nullReducer,
    unwatchRequest: nullReducer,
  }

  return createSlice({
    name,
    initialState: { ...crudInitialState, ...initialState },
    reducers: { ...crudReducers, ...reducers },
  })
}

export const createCrudSelectors = ({ name }) => {
  const crudSelectors = {
    isFetching: (state) => state[name].fetching,
    isError: (state) => state[name].error,
    getProblem: (state) => state[name].problem,
    getTotal: (state) => state[name].total,
  }
  crudSelectors['get' + name.charAt(0).toUpperCase() + name.slice(1)] =
    (state) => state[name][name]

  crudSelectors['getQueried' + name.charAt(0).toUpperCase() + name.slice(1)] = (state) =>
    state[name].query
      ? state[name].queryIds.map((id) => state[name][name].find((val) => val._id === id))
      : state[name][name]

  crudSelectors['get' + name.charAt(0).toUpperCase() + name.slice(1, -1)] = (id) =>
    (state) => state[name][name].find((v) => v._id === id)

  return crudSelectors
}
